/**
 * PRODA Finance API Client
 *
 * Provides typed API methods for communicating with the PRODA Finance backend.
 */

import type {
    TApiAllowedSegmentValues,
    TApiAnnouncement,
    TApiAuditLogEntry,
    TApiAuditLogFilterOptions,
    TApiAutoPopulatePropcodesByFilterRequest,
    TApiAutoPopulatePropcodesRequest,
    TApiAutoPopulatePropcodesResponse,
    TApiAutoPopulateSegmentsByFilterRequest,
    TApiAutoPopulateSegmentsRequest,
    TApiAutoPopulateSegmentsResponse,
    TApiBook,
    TApiBookMasterUploadResponse,
    TApiBusinessPartner,
    TApiBusinessPartnerFilterOptions,
    TApiBusinessPartnerType,
    TApiChartOfAccounts,
    TApiCoAMapping,
    TApiCoAMasterUploadResponse,
    TApiCoAUploadResponse,
    TApiColumnExtractionResponse,
    TApiColumnMappingFileType,
    TApiColumnMappingResponse,
    TApiCommitResponse,
    TApiDealFileType,
    TApiCompany,
    TApiCompanyAuditLogEntry,
    TApiCompanySftpSettings,
    TApiCompanySftpSettingsGetResponse,
    TApiCompanyYardiMasterDataSettings,
    TApiCompanyYardiMasterDataSettingsGetResponse,
    TApiCompanyYardiMasterDataSettingsUpdate,
    TApiContributorEntry,
    TApiDeal,
    TApiDealCountsByBusinessPartner,
    TApiDealFilterOptions,
    TApiDealPropcodeMapping,
    TApiDealPropertyCodeOption,
    TApiDealSegmentMapping,
    TApiEmailLogEntry,
    TApiExportResponse,
    TApiFileFilterOptions,
    TApiFileLineItem,
    TApiFileLineItemsResponse,
    TApiFileOperationAccepted,
    TApiFileOperationStatus,
    TApiFileProcessingThreshold,
    TApiFileSftpSubmission,
    TApiFileStatus,
    TApiFilteringRulesResponse,
    TApiFilteringRulesUpdate,
    TApiFinancialFile,
    TApiHealthResponse,
    TApiHomepageBoard,
    TApiHomepageView,
    TApiLineItemFilterOptions,
    TApiLineItemQuery,
    TApiMasterCodeUsageResponse,
    TApiOTBInfoResponse,
    TApiOTBPreValidationResponse,
    TApiOTBUploadResponse,
    TApiOperatingStatement,
    TApiOriginalPreview,
    TApiComparisonQuery,
    TApiComparisonResponse,
    TApiPaginatedChartOfAccounts,
    TApiPaginatedPropcodeVersions,
    TApiPaginatedPropertyCodes,
    TApiPaginatedSegmentVersions,
    TApiPortfolioCompany,
    TApiPropcodeMappingCreate,
    TApiPropcodeMappingUpdate,
    TApiPropcodeMappingValidationResponse,
    TApiPropcodeMasterUploadResponse,
    TApiPropertyCode,
    TApiRevalidationStatus,
    TApiReverseSplitResponse,
    TApiSegmentCustomValue,
    TApiSegmentMappingCreate,
    TApiSegmentMappingUpdate,
    TApiSegmentMappingValidationResponse,
    TApiSegmentMasterUploadResponse,
    TApiSegmentOption,
    TApiSegmentRulesUploadResponse,
    TApiSegmentValue,
    TApiSegmentationMapping,
    TApiServiceProvider,
    TApiSftpConnectionTestResponse,
    TApiSftpFileSubmission,
    TApiSplitLineItemRequest,
    TApiSplitLineItemResponse,
    TApiSubmissionContext,
    TApiSubmissionHistoryResponse,
    TApiSupportTicketCommentResponse,
    TApiSupportTicketListResponse,
    TApiSupportTicketResponse,
    TApiSupportTicketStatus,
    TApiSupportingFile,
    TApiSupportingFileFilterOptions,
    TApiSyncResponse,
    TApiUser,
    TApiUserFeatureFlags,
    TApiUserFeatureFlagsUpdate,
    TApiVersionRestoreResponse,
    TApiVersionUploadResponse,
    TApiYardiAccountOption,
    TApiYardiFileUploadResponse,
    TApiYardiGenerateResponse,
    TApiYardiImportStatus,
    TApiYardiMasterDataSyncRunList,
    TApiYardiMasterDataSyncTriggered,
    TAuditLogExportParams,
    TAuditLogQuery,
    TCreateAnnouncementRequest,
    TCreateBookRequest,
    TCreateChartOfAccountsRequest,
    TCreateCompanyRequest,
    TCreateDealRequest,
    TCreateFeedbackRequest,
    TCreateFileProcessingThresholdRequest,
    TCreateFileSftpSubmissionRequest,
    TCreatePortfolioCompanyRequest,
    TCreatePropertyCodeRequest,
    TCreateSegmentCustomValueRequest,
    TCreateSegmentValueRequest,
    TCreateSegmentationMappingRequest,
    TCreateServiceProviderRequest,
    TCreateSupportTicketCommentRequest,
    TCreateSupportTicketRequest,
    TCreateUserRequest,
    TEmailLogQuery,
    TExportRequest,
    TFeedbackResponse,
    TFileUploadRequest,
    TPaginatedResponse,
    TCappedPaginatedResponse,
    TPatchUserRequest,
    TSupportTicketAdminActionRequest,
    TUUID,
    TUpdateAnnouncementRequest,
    TUpdateBookRequest,
    TUpdateChartOfAccountsRequest,
    TUpdateColumnMappingsRequest,
    TUpdateCompanyConfigRequest,
    TApiCustomerModule,
    TUpdateCompanyRequest,
    TUpdateCompanySftpSettingsRequest,
    TUpdateDealRequest,
    TUpdateFileProcessingThresholdRequest,
    TUpdatePortfolioCompanyRequest,
    TUpdatePropertyCodeRequest,
    TUpdateSegmentCustomValueRequest,
    TUpdateSegmentValueRequest,
    TUpdateSegmentationMappingRequest,
    TUpdateServiceProviderRequest,
    TUpdateSupportTicketRequest,
} from "./types";

/**
 * Request interceptor type
 */
export type TRequestInterceptor = (request: RequestInit) => RequestInit | Promise<RequestInit>;

/**
 * API client configuration
 */
export type TApiClientConfig = {
    baseUrl: string;
    companyId?: string;
    requestInterceptors?: TRequestInterceptor[];
    /**
     * Slug of the acting company's customer module, read on every request:
     * customer module routes go to `/v1/m/<slug>/...`. Unset or null means
     * Rev EU, the only module routes were built for before customer modules.
     */
    getCustomerModule?: () => string | null | undefined;
};

/**
 * API Error class
 */
export class ApiError extends Error {
    constructor(
        message: string,
        public status: number,
        public response?: unknown,
    ) {
        super(message);
        this.name = "ApiError";
    }
}

type TProdaJsonErrorBody = {
    error?: {
        code?: string;
        message?: string;
        category?: string;
        retryable?: boolean;
        request_id?: string | null;
        details?: Record<string, unknown>;
    };
};

/**
 * Extract a user-visible message from PRODA API JSON error bodies (used by fetch client).
 */
/** The customer module whose routes the client calls when none is known. */
export const DEFAULT_CUSTOMER_MODULE = "rev_eu";

/**
 * Customer-module route prefixes (under the API base URL): every company-scoped
 * route of customer module `slug` lives at `/v1/m/<slug>/<instance>/...`.
 * Modules copied from Rev EU (LaSalle) serve the same instances and routes.
 */
export const customerModuleApi = (slug: string | null | undefined) => {
    const base = `/v1/m/${slug || DEFAULT_CUSTOMER_MODULE}`;
    return {
        partners: `${base}/partners`,
        master_data: `${base}/master_data`,
        mappings: `${base}/mappings`,
        workflow: `${base}/workflow`,
        validation: `${base}/validation`,
        ingestion: `${base}/ingestion`,
        review: `${base}/review`,
        generation: `${base}/generation`,
        submissions: `${base}/submissions`,
        delivery: `${base}/delivery`,
        attachments: `${base}/attachments`,
    } as const;
};

/** Rev EU's route prefixes. */
export const REV_EU_API = customerModuleApi(DEFAULT_CUSTOMER_MODULE);

export function messageFromProdaErrorBody(errorBody: unknown, httpStatus: number): string {
    if (errorBody === null || errorBody === undefined) {
        return `HTTP ${httpStatus}`;
    }
    if (typeof errorBody === "string") {
        return errorBody;
    }
    if (typeof errorBody !== "object") {
        return `HTTP ${httpStatus}`;
    }
    const body = errorBody as TProdaJsonErrorBody;
    if (typeof body.error?.message === "string") {
        return body.error.message;
    }
    return `HTTP ${httpStatus}`;
}

/**
 * Create the PRODA Finance API client
 */
export function createApiClient(config: TApiClientConfig) {
    const { baseUrl, companyId, requestInterceptors = [], getCustomerModule } = config;
    // The acting company's module prefixes, read on every access, so switching
    // the company viewed-as switches modules.
    const MODULE_API = {} as ReturnType<typeof customerModuleApi>;
    for (const instance of Object.keys(REV_EU_API) as (keyof typeof REV_EU_API)[]) {
        Object.defineProperty(MODULE_API, instance, {
            get: () => customerModuleApi(getCustomerModule?.())[instance],
        });
    }

    /**
     * Handle 401 Unauthorized errors by triggering re-login
     */
    async function handleAuthError(status: number): Promise<void> {
        if (status === 401) {
            const { tokenService } = await import("@/auth/tokenService");
            tokenService.notifyAuthError();
        }
    }

    /**
     * Wrapper around fetch that automatically handles auth errors.
     * Use this for all raw fetch calls to ensure 401 responses trigger re-login.
     */
    async function authFetch(url: string, init?: RequestInit): Promise<Response> {
        const response = await fetch(url, init);
        if (!response.ok) {
            await handleAuthError(response.status);
        }
        return response;
    }

    /**
     * Make a fetch request with interceptors
     */
    async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
        const url = `${baseUrl}${path}`;

        let requestInit: RequestInit = {
            ...options,
            headers: {
                "Content-Type": "application/json",
                ...(companyId && { "X-Company-ID": companyId }),
                ...options.headers,
            },
        };

        // Apply interceptors
        for (const interceptor of requestInterceptors) {
            requestInit = await interceptor(requestInit);
        }

        // authFetch automatically handles 401 errors
        const response = await authFetch(url, requestInit);

        if (!response.ok) {
            let errorBody: unknown;
            const text = await response.text();
            try {
                errorBody = JSON.parse(text);
            } catch {
                errorBody = text;
            }

            const errorMessage = messageFromProdaErrorBody(errorBody, response.status);

            // A deactivated account returns 403 USER_INACTIVE on every request.
            // Surface a full-page notice (mirrors the 401 → re-login handling)
            // rather than letting each view render its own permission error.
            if (
                response.status === 403 &&
                errorBody !== null &&
                typeof errorBody === "object" &&
                (errorBody as TProdaJsonErrorBody).error?.code === "USER_INACTIVE"
            ) {
                const { tokenService } = await import("@/auth/tokenService");
                tokenService.notifyInactiveAccount({ message: errorMessage });
            }

            throw new ApiError(errorMessage, response.status, errorBody);
        }

        // Handle empty responses
        if (response.status === 204) {
            return undefined as T;
        }

        return response.json();
    }

    /**
     * Build query string from params object
     */
    function buildQuery(params: Record<string, unknown>): string {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null) continue;
            if (Array.isArray(value)) {
                for (const item of value) {
                    if (item !== undefined && item !== null) {
                        searchParams.append(key, String(item));
                    }
                }
            } else {
                searchParams.append(key, String(value));
            }
        }
        const queryString = searchParams.toString();
        return queryString ? `?${queryString}` : "";
    }

    /**
     * Fetch a file-producing endpoint and hand the bytes to the browser as a download.
     *
     * Keyed on the path rather than a download-type union so every route that
     * returns a file body can share it — the original/processed/errors downloads
     * and the generated ETL exports, whose URL shapes differ.
     */
    async function downloadFromPath(path: string): Promise<void> {
        const file = await fetchFileFromPath(path);
        const downloadUrl = window.URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }

    /**
     * Fetch a file-producing endpoint and return the body as a `File`, named from
     * `Content-Disposition`, without handing it to the browser as a download.
     */
    async function fetchFileFromPath(path: string): Promise<File> {
        const url = `${baseUrl}${path}`;
        let requestInit: RequestInit = {
            method: "GET",
            headers: {
                ...(companyId && { "X-Company-ID": companyId }),
            },
        };

        for (const interceptor of requestInterceptors) {
            requestInit = await interceptor(requestInit);
        }

        const response = await authFetch(url, requestInit);
        if (!response.ok) {
            const errorBody = await response.json().catch(() => undefined);
            const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
            throw new ApiError(errorMessage, response.status, errorBody);
        }
        const contentDisposition = response.headers.get("Content-Disposition");
        let filename = "download";
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?([^;]+)/i);
            if (filenameMatch?.[1]) {
                filename = decodeURIComponent(filenameMatch[1].replace(/["']/g, ""));
            }
        }

        const blob = await response.blob();
        return new File([blob], filename, { type: blob.type });
    }

    return {
        // Health
        health: {
            check: () => request<TApiHealthResponse>("/v1/health"),
            ready: () => request<{ ready: boolean }>("/v1/health/ready"),
        },

        // Homepage v2
        homepage: {
            getBoard: (
                params?: {
                    view?: TApiHomepageView;
                    periods?: string[];
                    business_partner_type?: TApiBusinessPartnerType;
                    business_partner_id?: TUUID;
                    deal_id?: TUUID;
                    finance_user_id?: TUUID;
                    /** Rows per column; raised by the board's "Show more". */
                    row_limit?: number;
                },
                signal?: AbortSignal,
            ) =>
                request<TApiHomepageBoard>(
                    `${MODULE_API.submissions}/homepage/board${buildQuery(params || {})}`,
                    {
                        signal,
                    },
                ),
        },

        // Companies (admin only)
        companies: {
            list: (
                params?: {
                    skip?: number;
                    limit?: number;
                    name?: string;
                    code?: string;
                    contact?: string;
                    email_domains?: string;
                    is_active?: boolean;
                    sort_by?: string;
                    sort_dir?: string;
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiCompany>>(
                    `/v1/companies${buildQuery(params || {})}`,
                    {
                        signal,
                    },
                ),
            /** Every registered customer module, for the Config modal dropdown (Proda admin only) */
            customerModules: () => request<TApiCustomerModule[]>("/v1/companies/customer-modules"),
            get: (id: TUUID) => request<TApiCompany>(`/v1/companies/${id}`),
            create: (data: TCreateCompanyRequest) =>
                request<TApiCompany>("/v1/companies", {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdateCompanyRequest) =>
                request<TApiCompany>(`/v1/companies/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            /** Update a company's feature flags and customer module (Proda admin only) */
            updateConfig: (id: TUUID, data: TUpdateCompanyConfigRequest) =>
                request<TApiCompany>(`/v1/companies/${id}/config`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            delete: (id: TUUID) => request<void>(`/v1/companies/${id}`, { method: "DELETE" }),
            activate: (id: TUUID) =>
                request<TApiCompany>(`/v1/companies/${id}/activate`, { method: "POST" }),
            deactivate: (id: TUUID) =>
                request<TApiCompany>(`/v1/companies/${id}/deactivate`, { method: "POST" }),
            uploadLogo: async (id: TUUID, logo: File): Promise<TApiCompany> => {
                const formData = new FormData();
                formData.append("logo", logo);

                const url = `${baseUrl}/v1/companies/${id}/logo`;
                let requestInit: RequestInit = {
                    method: "PUT",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            deleteLogo: (id: TUUID) =>
                request<TApiCompany>(`/v1/companies/${id}/logo`, { method: "DELETE" }),
        },

        // Users
        users: {
            list: (
                params?: {
                    page?: number;
                    page_size?: number;
                    is_active?: boolean;
                    company_id?: TUUID;
                    search?: string;
                    sort_by?: string;
                    sort_dir?: string;
                    finance_team?: boolean;
                    business_partners?: boolean;
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiUser>>(`/v1/users${buildQuery(params || {})}`, {
                    signal,
                }),
            get: (id: TUUID) => request<TApiUser>(`/v1/users/${id}`),
            me: () => request<TApiUser>("/v1/users/me"),
            create: (data: TCreateUserRequest) =>
                request<TApiUser>("/v1/users", {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TPatchUserRequest) =>
                request<TApiUser>(`/v1/users/${id}`, {
                    method: "PATCH",
                    body: JSON.stringify(data),
                }),
            delete: (id: TUUID) => request<void>(`/v1/users/${id}`, { method: "DELETE" }),
            activate: (id: TUUID) =>
                request<TApiUser>(`/v1/users/${id}/activate`, { method: "POST" }),
            deactivate: (id: TUUID) =>
                request<TApiUser>(`/v1/users/${id}/deactivate`, { method: "POST" }),
            // Proda users only (403 otherwise): per-user feature-flag overrides.
            getFeatureFlags: (id: TUUID) =>
                request<TApiUserFeatureFlags>(`/v1/users/${id}/feature-flags`),
            updateFeatureFlags: (id: TUUID, data: TApiUserFeatureFlagsUpdate) =>
                request<TApiUserFeatureFlags>(`/v1/users/${id}/feature-flags`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
        },

        // Service Providers
        serviceProviders: {
            list: (params?: {
                page?: number;
                page_size?: number;
                is_active?: boolean;
                skip?: number;
                limit?: number;
                business_partner_type?: string;
                name?: string;
                can_upload_supporting_files?: boolean;
                sort_by?: string;
                sort_dir?: string;
            }) =>
                request<TPaginatedResponse<TApiServiceProvider>>(
                    `${MODULE_API.partners}/service-providers${buildQuery(params || {})}`,
                ),
            get: (id: TUUID) =>
                request<TApiServiceProvider>(`${MODULE_API.partners}/service-providers/${id}`),
            create: (data: TCreateServiceProviderRequest) =>
                request<TApiServiceProvider>(`${MODULE_API.partners}/service-providers`, {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdateServiceProviderRequest) =>
                request<TApiServiceProvider>(`${MODULE_API.partners}/service-providers/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            delete: (id: TUUID) =>
                request<void>(`${MODULE_API.partners}/service-providers/${id}`, {
                    method: "DELETE",
                }),
            activate: (id: TUUID) =>
                request<TApiServiceProvider>(
                    `${MODULE_API.partners}/service-providers/${id}/activate`,
                    {
                        method: "POST",
                    },
                ),
            deactivate: (id: TUUID) =>
                request<TApiServiceProvider>(
                    `${MODULE_API.partners}/service-providers/${id}/deactivate`,
                    {
                        method: "POST",
                    },
                ),
            setEmployees: (id: TUUID, employeeIds: TUUID[]) =>
                request<TApiServiceProvider>(
                    `${MODULE_API.partners}/service-providers/${id}/employees`,
                    {
                        method: "PUT",
                        body: JSON.stringify({ employee_ids: employeeIds }),
                    },
                ),
            // CoA (Chart of Accounts) mappings
            listCoAMappings: (
                id: TUUID,
                params?: {
                    page?: number;
                    page_size?: number;
                    active_only?: boolean;
                    source_account_code?: string;
                    source_account_code_2?: string;
                    source_account_name?: string;
                    level?: "A" | "P" | "C";
                    yardi_account_code?: string;
                    yardi_account_name?: string;
                    sort_by?: string;
                    sort_dir?: "asc" | "desc";
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiCoAMapping>>(
                    `${MODULE_API.partners}/service-providers/${id}/coa${buildQuery(params || {})}`,
                    { signal },
                ),
            getCoAMappingCount: (id: TUUID) =>
                request<{ count: number }>(
                    `${MODULE_API.partners}/service-providers/${id}/coa/count`,
                ),
            updateCoAMapping: (
                id: TUUID,
                mappingId: TUUID,
                data: {
                    level?: "A" | "P" | "C";
                    is_active?: boolean;
                    yardi_account_id?: string;
                    is_in_force?: boolean;
                },
            ) => {
                const body: Record<string, string | boolean> = {};
                if (data.level !== undefined) body["level"] = data.level;
                if (data.is_active !== undefined) body["is_active"] = data.is_active;
                if (data.yardi_account_id !== undefined)
                    body["yardi_account_id"] = data.yardi_account_id;
                if (data.is_in_force !== undefined) body["is_in_force"] = data.is_in_force;
                return request<TApiCoAMapping>(
                    `${MODULE_API.partners}/service-providers/${id}/coa/mappings/${mappingId}`,
                    { method: "PUT", body: JSON.stringify(body) },
                );
            },
            createCoAMapping: (
                id: TUUID,
                data: {
                    third_account_code: string;
                    third_account_name: string;
                    yardi_account_id: string;
                    level: "A" | "P" | "C";
                },
            ) =>
                request<TApiCoAMapping>(
                    `${MODULE_API.partners}/service-providers/${id}/coa/mappings`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                ),
            deleteCoAMapping: (id: TUUID, mappingId: TUUID) =>
                request<void>(
                    `${MODULE_API.partners}/service-providers/${id}/coa/mappings/${mappingId}`,
                    {
                        method: "DELETE",
                    },
                ),
            uploadCoA: async (id: TUUID, file: File): Promise<TApiCoAUploadResponse> => {
                const formData = new FormData();
                formData.append("file", file);

                const url = `${baseUrl}${MODULE_API.mappings}/service-providers/${id}/coa/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => ({}));
                    const parsed = messageFromProdaErrorBody(errorBody, response.status);
                    const errorMessage =
                        parsed.startsWith("HTTP ") && parsed === `HTTP ${response.status}`
                            ? "CoA upload failed"
                            : parsed;
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            createWithCoa: async (
                data: TCreateServiceProviderRequest,
                file: File,
            ): Promise<TApiServiceProvider> => {
                const form = new FormData();
                form.append("data", JSON.stringify(data));
                form.append("file", file);

                const url = `${baseUrl}${MODULE_API.partners}/service-providers/with-coa`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: form,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => ({}));
                    const parsed = messageFromProdaErrorBody(errorBody, response.status);
                    const errorMessage =
                        parsed.startsWith("HTTP ") && parsed === `HTTP ${response.status}`
                            ? "Business partner creation with CoA failed"
                            : parsed;
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            searchYardiAccounts: (
                spId: string,
                params?: {
                    search?: string;
                    page?: number;
                    page_size?: number;
                    active_only?: boolean;
                },
            ) =>
                request<TPaginatedResponse<TApiYardiAccountOption>>(
                    `${MODULE_API.partners}/service-providers/${spId}/coa-mappings/yardi-accounts${buildQuery(
                        {
                            search: params?.search,
                            page: params?.page,
                            page_size: params?.page_size,
                            active_only: params?.active_only,
                        },
                    )}`,
                ),
        },

        // Business Partners (unified SP+PC endpoint)
        businessPartners: {
            list: (
                params?: {
                    skip?: number;
                    limit?: number;
                    name?: string;
                    business_partner_ids?: string[];
                    employee_ids?: string[];
                    employees_unassigned?: boolean;
                    business_partner_type?: string;
                    can_upload_supporting_files?: boolean;
                    is_active?: boolean;
                    sort_by?: string;
                    sort_dir?: string;
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiBusinessPartner>>(
                    `${MODULE_API.partners}/business-partners${buildQuery(params || {})}`,
                    { signal },
                ),
            filterOptions: () =>
                request<TApiBusinessPartnerFilterOptions>(
                    `${MODULE_API.partners}/business-partners/filter-options`,
                ),
        },

        // Portfolio Companies
        portfolioCompanies: {
            list: (params?: { skip?: number; limit?: number; active_only?: boolean }) =>
                request<TPaginatedResponse<TApiPortfolioCompany>>(
                    `${MODULE_API.partners}/portfolio-companies${buildQuery(params || {})}`,
                ),
            get: (id: TUUID) =>
                request<TApiPortfolioCompany>(`${MODULE_API.partners}/portfolio-companies/${id}`),
            create: (data: TCreatePortfolioCompanyRequest) =>
                request<TApiPortfolioCompany>(`${MODULE_API.partners}/portfolio-companies`, {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdatePortfolioCompanyRequest) =>
                request<TApiPortfolioCompany>(`${MODULE_API.partners}/portfolio-companies/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            delete: (id: TUUID) =>
                request<void>(`${MODULE_API.partners}/portfolio-companies/${id}`, {
                    method: "DELETE",
                }),
            activate: (id: TUUID) =>
                request<TApiPortfolioCompany>(
                    `${MODULE_API.partners}/portfolio-companies/${id}/activate`,
                    {
                        method: "POST",
                    },
                ),
            deactivate: (id: TUUID) =>
                request<TApiPortfolioCompany>(
                    `${MODULE_API.partners}/portfolio-companies/${id}/deactivate`,
                    {
                        method: "POST",
                    },
                ),
            setEmployees: (id: TUUID, employeeIds: TUUID[]) =>
                request<TApiPortfolioCompany>(
                    `${MODULE_API.partners}/portfolio-companies/${id}/employees`,
                    {
                        method: "PUT",
                        body: JSON.stringify({ employee_ids: employeeIds }),
                    },
                ),
            listCoAMappings: (
                id: TUUID,
                params?: {
                    page?: number;
                    page_size?: number;
                    active_only?: boolean;
                    source_account_code?: string;
                    source_account_code_2?: string;
                    source_account_name?: string;
                    level?: "A" | "P" | "C";
                    yardi_account_code?: string;
                    yardi_account_name?: string;
                    sort_by?: string;
                    sort_dir?: "asc" | "desc";
                },
            ) =>
                request<TPaginatedResponse<TApiCoAMapping>>(
                    `${MODULE_API.partners}/portfolio-companies/${id}/coa${buildQuery(params || {})}`,
                ),
            updateCoAMapping: (
                id: TUUID,
                mappingId: TUUID,
                data: {
                    level?: "A" | "P" | "C";
                    is_active?: boolean;
                    yardi_account_id?: string;
                    is_in_force?: boolean;
                },
            ) => {
                const body: Record<string, string | boolean> = {};
                if (data.level !== undefined) body["level"] = data.level;
                if (data.is_active !== undefined) body["is_active"] = data.is_active;
                if (data.yardi_account_id !== undefined)
                    body["yardi_account_id"] = data.yardi_account_id;
                if (data.is_in_force !== undefined) body["is_in_force"] = data.is_in_force;
                return request<TApiCoAMapping>(
                    `${MODULE_API.partners}/portfolio-companies/${id}/coa/mappings/${mappingId}`,
                    { method: "PUT", body: JSON.stringify(body) },
                );
            },
            createCoAMapping: (
                id: TUUID,
                data: {
                    third_account_code: string;
                    third_account_name: string;
                    yardi_account_id: string;
                    level: "A" | "P" | "C";
                },
            ) =>
                request<TApiCoAMapping>(
                    `${MODULE_API.partners}/portfolio-companies/${id}/coa/mappings`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                ),
            deleteCoAMapping: (id: TUUID, mappingId: TUUID) =>
                request<void>(
                    `${MODULE_API.partners}/portfolio-companies/${id}/coa/mappings/${mappingId}`,
                    {
                        method: "DELETE",
                    },
                ),
            uploadCoA: async (id: TUUID, file: File): Promise<TApiCoAUploadResponse> => {
                const formData = new FormData();
                formData.append("file", file);

                const url = `${baseUrl}${MODULE_API.mappings}/portfolio-companies/${id}/coa/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => ({}));
                    const parsed = messageFromProdaErrorBody(errorBody, response.status);
                    const errorMessage =
                        parsed.startsWith("HTTP ") && parsed === `HTTP ${response.status}`
                            ? "CoA upload failed"
                            : parsed;
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            createWithCoa: async (
                data: TCreatePortfolioCompanyRequest,
                file: File,
            ): Promise<TApiPortfolioCompany> => {
                const form = new FormData();
                form.append("data", JSON.stringify(data));
                form.append("file", file);

                const url = `${baseUrl}${MODULE_API.partners}/portfolio-companies/with-coa`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: form,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => ({}));
                    const parsed = messageFromProdaErrorBody(errorBody, response.status);
                    const errorMessage =
                        parsed.startsWith("HTTP ") && parsed === `HTTP ${response.status}`
                            ? "Business partner creation with CoA failed"
                            : parsed;
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            searchYardiAccounts: (
                pcId: string,
                params?: {
                    search?: string;
                    page?: number;
                    page_size?: number;
                    active_only?: boolean;
                },
            ) =>
                request<TPaginatedResponse<TApiYardiAccountOption>>(
                    `${MODULE_API.partners}/portfolio-companies/${pcId}/coa-mappings/yardi-accounts${buildQuery(
                        {
                            search: params?.search,
                            page: params?.page,
                            page_size: params?.page_size,
                            active_only: params?.active_only,
                        },
                    )}`,
                ),
        },

        // Deals
        deals: {
            list: (
                params?: {
                    skip?: number;
                    limit?: number;
                    is_active?: boolean;
                    service_provider_id?: TUUID;
                    portfolio_company_id?: TUUID;
                    business_partner_id?: TUUID;
                    sp_active_only?: boolean;
                    can_upload_sf_only?: boolean;
                    name?: string;
                    deal_file_type?: string;
                    created_from?: string;
                    created_to?: string;
                    sort_by?: string;
                    sort_dir?: string;
                    active_only?: boolean;
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiDeal>>(
                    `${MODULE_API.partners}/deals${buildQuery(params || {})}`,
                    {
                        signal,
                    },
                ),
            countsByBusinessPartner: () =>
                request<TApiDealCountsByBusinessPartner>(
                    `${MODULE_API.partners}/deals/counts-by-business-partner`,
                ),
            filterOptions: () =>
                request<TApiDealFilterOptions>(`${MODULE_API.partners}/deals/filter-options`),
            get: (id: TUUID) => request<TApiDeal>(`${MODULE_API.partners}/deals/${id}`),
            create: (data: TCreateDealRequest) =>
                request<TApiDeal>(`${MODULE_API.partners}/deals`, {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdateDealRequest) =>
                request<TApiDeal>(`${MODULE_API.partners}/deals/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            delete: (id: TUUID) =>
                request<void>(`${MODULE_API.partners}/deals/${id}`, { method: "DELETE" }),
            activate: (id: TUUID) =>
                request<TApiDeal>(`${MODULE_API.partners}/deals/${id}/activate`, {
                    method: "POST",
                }),
            deactivate: (id: TUUID) =>
                request<TApiDeal>(`${MODULE_API.partners}/deals/${id}/deactivate`, {
                    method: "POST",
                }),
            getSegmentMappings: (id: TUUID, params?: { skip?: number; limit?: number }) =>
                request<TPaginatedResponse<TApiDealSegmentMapping>>(
                    `${MODULE_API.partners}/deals/${id}/segment-mappings${buildQuery(params || {})}`,
                ),
            getPropcodeMappings: (id: TUUID, params?: { skip?: number; limit?: number }) =>
                request<TPaginatedResponse<TApiDealPropcodeMapping>>(
                    `${MODULE_API.partners}/deals/${id}/propcode-mappings${buildQuery(params || {})}`,
                ),
            getColumnMappings: (
                dealId: TUUID,
                params?: { file_type?: TApiColumnMappingFileType; skip?: number; limit?: number },
            ) =>
                request<TPaginatedResponse<TApiColumnMappingResponse>>(
                    `${MODULE_API.partners}/deals/${dealId}/column-mappings${buildQuery(params || {})}`,
                ),
            updateColumnMappings: (dealId: TUUID, data: TUpdateColumnMappingsRequest) =>
                request<TApiColumnMappingResponse[]>(
                    `${MODULE_API.partners}/deals/${dealId}/column-mappings`,
                    {
                        method: "PUT",
                        body: JSON.stringify(data),
                    },
                ),
            getFilteringRules: (dealId: TUUID, fileType: TApiColumnMappingFileType) =>
                request<TApiFilteringRulesResponse>(
                    `${MODULE_API.partners}/deals/${dealId}/filtering-rules${buildQuery({ file_type: fileType })}`,
                ),
            // Filtering rules are NOT part of the deal update payload: the backend
            // gates them on tb/gl column-edit rights via this dedicated endpoint.
            updateFilteringRules: (dealId: TUUID, data: TApiFilteringRulesUpdate) =>
                request<TApiFilteringRulesResponse>(
                    `${MODULE_API.partners}/deals/${dealId}/filtering-rules`,
                    {
                        method: "PUT",
                        body: JSON.stringify(data),
                    },
                ),
            getPropcodeVersions: (dealId: TUUID, params?: { skip?: number; limit?: number }) =>
                request<TApiPaginatedPropcodeVersions>(
                    `${MODULE_API.master_data}/deals/${dealId}/propcode-mappings/versions${buildQuery(params || {})}`,
                ),
            uploadPropcodeVersion: async (
                dealId: TUUID,
                file: File,
                description?: string,
            ): Promise<TApiVersionUploadResponse> => {
                const formData = new FormData();
                formData.append("file", file);
                if (description !== undefined && description !== "")
                    formData.append("description", description);
                const url = `${baseUrl}${MODULE_API.master_data}/deals/${dealId}/propcode-mappings/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: { ...(companyId && { "X-Company-ID": companyId }) },
                };
                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }
                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const msg = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(msg, response.status, errorBody);
                }
                return response.json();
            },
            createPropcodeSnapshot: (dealId: TUUID, description?: string) =>
                request<TApiVersionUploadResponse>(
                    `${MODULE_API.master_data}/deals/${dealId}/propcode-mappings/snapshot`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(description != null ? { description } : {}),
                    },
                ),
            restorePropcodeVersion: (dealId: TUUID, versionId: TUUID) =>
                request<TApiVersionRestoreResponse>(
                    `${MODULE_API.master_data}/deals/${dealId}/propcode-mappings/versions/${versionId}/restore`,
                    { method: "PUT" },
                ),
            updatePropcodeMapping: (
                dealId: TUUID,
                mappingId: TUUID,
                data: TApiPropcodeMappingUpdate,
            ) =>
                request<TApiDealPropcodeMapping>(
                    `${MODULE_API.partners}/deals/${dealId}/propcode-mappings/${mappingId}`,
                    {
                        method: "PUT",
                        body: JSON.stringify(data),
                    },
                ),
            createPropcodeMapping: (dealId: TUUID, data: TApiPropcodeMappingCreate) =>
                request<TApiDealPropcodeMapping>(
                    `${MODULE_API.partners}/deals/${dealId}/propcode-mappings`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                ),
            deletePropcodeMapping: (dealId: TUUID, mappingId: TUUID) =>
                request<void>(
                    `${MODULE_API.partners}/deals/${dealId}/propcode-mappings/${mappingId}`,
                    {
                        method: "DELETE",
                    },
                ),
            // Segment version management
            getSegmentVersions: (
                dealId: TUUID,
                segmentNumber: number,
                params?: { skip?: number; limit?: number },
            ) =>
                request<TApiPaginatedSegmentVersions>(
                    `${MODULE_API.master_data}/deals/${dealId}/segment-mappings/${segmentNumber}/versions${buildQuery(params || {})}`,
                ),
            uploadSegmentVersion: async (
                dealId: TUUID,
                segmentNumber: number,
                file: File,
                description?: string,
            ): Promise<TApiVersionUploadResponse> => {
                const formData = new FormData();
                formData.append("file", file);
                if (description !== undefined && description !== "")
                    formData.append("description", description);
                const url = `${baseUrl}${MODULE_API.master_data}/deals/${dealId}/segment-mappings/${segmentNumber}/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: { ...(companyId && { "X-Company-ID": companyId }) },
                };
                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }
                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const msg = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(msg, response.status, errorBody);
                }
                return response.json();
            },
            createSegmentSnapshot: (dealId: TUUID, segmentNumber: number, description?: string) =>
                request<TApiVersionUploadResponse>(
                    `${MODULE_API.master_data}/deals/${dealId}/segment-mappings/${segmentNumber}/snapshot`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(description != null ? { description } : {}),
                    },
                ),
            restoreSegmentVersion: (dealId: TUUID, segmentNumber: number, versionId: TUUID) =>
                request<TApiVersionRestoreResponse>(
                    `${MODULE_API.master_data}/deals/${dealId}/segment-mappings/${segmentNumber}/versions/${versionId}/restore`,
                    { method: "PUT" },
                ),
            // Segment mapping CRUD
            createSegmentMapping: (dealId: TUUID, data: TApiSegmentMappingCreate) =>
                request<TApiDealSegmentMapping>(
                    `${MODULE_API.partners}/deals/${dealId}/segment-mappings`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                ),
            updateSegmentMapping: (
                dealId: TUUID,
                mappingId: TUUID,
                data: TApiSegmentMappingUpdate,
            ) =>
                request<TApiDealSegmentMapping>(
                    `${MODULE_API.partners}/deals/${dealId}/segment-mappings/${mappingId}`,
                    {
                        method: "PUT",
                        body: JSON.stringify(data),
                    },
                ),
            deleteSegmentMapping: (dealId: TUUID, mappingId: TUUID) =>
                request<void>(
                    `${MODULE_API.partners}/deals/${dealId}/segment-mappings/${mappingId}`,
                    {
                        method: "DELETE",
                    },
                ),
            validateSegmentMappingFile: async (
                segmentNumber: number,
                file: File,
            ): Promise<TApiSegmentMappingValidationResponse> => {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("segment_number", String(segmentNumber));

                const url = `${baseUrl}${MODULE_API.partners}/deals/segment-mappings/validate`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            validatePropcodeMappingFile: async (
                file: File,
            ): Promise<TApiPropcodeMappingValidationResponse> => {
                const formData = new FormData();
                formData.append("file", file);

                const url = `${baseUrl}${MODULE_API.partners}/deals/propcode-mappings/validate`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            extractColumns: async (
                file: File,
                headerRowNumber: number = 1,
            ): Promise<TApiColumnExtractionResponse> => {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("header_row_number", String(headerRowNumber));

                const url = `${baseUrl}${MODULE_API.partners}/deals/columns/extract`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            // OTB (Opening Trial Balance) methods
            validateOTB: async (
                file: File,
                columnMappings: Array<{
                    file_type: string;
                    field_type: string;
                    column_name: string;
                    header_row_number: number;
                }>,
                startPeriod: string,
                filteringRules?: Array<{
                    filtering_column_header: string;
                    filtering_value: string[];
                    include_filtered_values: boolean;
                }>,
                dealFileType?: TApiDealFileType,
            ): Promise<TApiOTBPreValidationResponse> => {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("column_mappings_json", JSON.stringify(columnMappings));
                formData.append("start_period", startPeriod);
                if (filteringRules && filteringRules.length > 0) {
                    formData.append("filtering_rules_json", JSON.stringify(filteringRules));
                }
                if (dealFileType) {
                    formData.append("deal_file_type", dealFileType);
                }

                const url = `${baseUrl}${MODULE_API.partners}/deals/otb/validate`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            uploadOTB: async (
                dealId: TUUID,
                file: File,
                options?: { sheetName?: string; dateFormat?: string },
            ): Promise<TApiOTBUploadResponse> => {
                const formData = new FormData();
                formData.append("file", file);
                if (options?.sheetName) {
                    formData.append("sheet_name", options.sheetName);
                }
                if (options?.dateFormat) {
                    formData.append("date_format", options.dateFormat);
                }

                const url = `${baseUrl}${MODULE_API.partners}/deals/${dealId}/otb`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            getOTBInfo: (dealId: TUUID) =>
                request<TApiOTBInfoResponse>(`${MODULE_API.partners}/deals/${dealId}/otb`),
            deleteOTB: (dealId: TUUID) =>
                request<void>(`${MODULE_API.partners}/deals/${dealId}/otb`, { method: "DELETE" }),
            generatePlaceholderOTB: (dealId: TUUID, propertyCode: string) =>
                request<TApiOTBUploadResponse>(
                    `${MODULE_API.partners}/deals/${dealId}/otb/generate-placeholder`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ property_code: propertyCode }),
                    },
                ),
            searchPropertyCodes: (
                dealId: string,
                params?: { search?: string; page?: number; page_size?: number; kind?: string },
            ) =>
                request<TPaginatedResponse<TApiDealPropertyCodeOption>>(
                    `${MODULE_API.partners}/deals/${dealId}/property-codes${buildQuery({
                        search: params?.search,
                        page: params?.page,
                        page_size: params?.page_size,
                        kind: params?.kind,
                    })}`,
                ),
        },

        // Files
        files: {
            list: (
                params?: {
                    skip?: number;
                    limit?: number;
                    only_financial_journals?: boolean;
                    exclude_financial_journals?: boolean;
                    deal_id?: TUUID;
                    status?: TApiFileStatus;
                    file_type?: string;
                    period?: string;
                    service_provider_ids?: string[];
                    deal_ids?: string[];
                    original_filename?: string;
                    period_from?: string;
                    period_to?: string;
                    uploaded_from?: string;
                    uploaded_to?: string;
                    total_line_items_min?: string;
                    total_line_items_max?: string;
                    sort_by?: string;
                    sort_dir?: string;
                    archived?: boolean;
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiFinancialFile>>(
                    `${MODULE_API.ingestion}/files${buildQuery(params || {})}`,
                    { signal },
                ),
            filterOptions: (params?: {
                only_financial_journals?: boolean;
                exclude_financial_journals?: boolean;
            }) =>
                request<TApiFileFilterOptions>(
                    `${MODULE_API.ingestion}/files/filter-options${buildQuery(params || {})}`,
                ),
            get: (id: TUUID) => request<TApiFinancialFile>(`${MODULE_API.ingestion}/files/${id}`),
            // `period` is a date (YYYY-MM-DD), not YYYY-MM — the backend normalizes
            // it to the first of its month. Omit it to get the file's own period.
            submissionContext: (
                fileId: TUUID,
                params?: { period?: string },
                signal?: AbortSignal,
            ) =>
                request<TApiSubmissionContext>(
                    `${MODULE_API.submissions}/files/${fileId}/submission-context${buildQuery({
                        period: params?.period,
                    })}`,
                    { signal },
                ),
            // Same `period` contract as submissionContext: a YYYY-MM-DD date the
            // backend normalizes to the first of its month, omitted to get the
            // file's own period. Callers hold `YYYY-MM` and append the day —
            // see `loadSubmissionHistory`.
            submissionHistory: (
                fileId: TUUID,
                params?: { period?: string },
                signal?: AbortSignal,
            ) =>
                request<TApiSubmissionHistoryResponse>(
                    `${MODULE_API.submissions}/files/${fileId}/submission-history${buildQuery({
                        period: params?.period,
                    })}`,
                    { signal },
                ),
            originalPreview: (fileId: TUUID, params?: { sheet?: string }, signal?: AbortSignal) =>
                request<TApiOriginalPreview>(
                    `${MODULE_API.ingestion}/files/${fileId}/original-preview${buildQuery({ sheet: params?.sheet })}`,
                    { signal },
                ),
            operatingStatement: (
                fileId: TUUID,
                params?: { propertyCode?: string },
                signal?: AbortSignal,
            ) =>
                request<TApiOperatingStatement>(
                    `${MODULE_API.review}/files/${fileId}/operating-statement${buildQuery({
                        property_code: params?.propertyCode,
                    })}`,
                    { signal },
                ),
            comparison: (fileId: TUUID, query?: TApiComparisonQuery, signal?: AbortSignal) =>
                request<TApiComparisonResponse>(
                    `${MODULE_API.review}/files/${fileId}/comparison${buildQuery({ ...(query ?? {}) })}`,
                    { signal },
                ),
            upload: async (
                file: File,
                metadata: TFileUploadRequest,
            ): Promise<TApiFinancialFile> => {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("deal_id", metadata.deal_id);
                formData.append("period", metadata.period);
                formData.append("file_type", metadata.file_type);
                if (metadata.sheet_name) {
                    formData.append("sheet_name", metadata.sheet_name);
                }
                if (metadata.tb_sheet_name) {
                    formData.append("tb_sheet_name", metadata.tb_sheet_name);
                }
                if (metadata.gl_sheet_name) {
                    formData.append("gl_sheet_name", metadata.gl_sheet_name);
                }
                if (metadata.book_id) {
                    formData.append("book_id", metadata.book_id);
                }
                // Repeated form fields → FastAPI list[TUUID] = Form(...)
                for (const contributorId of metadata.contributor_ids ?? []) {
                    formData.append("contributor_ids", contributorId);
                }

                const url = `${baseUrl}${MODULE_API.ingestion}/files/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            delete: (id: TUUID) =>
                request<void>(`${MODULE_API.ingestion}/files/${id}`, { method: "DELETE" }),
            getLineItems: (fileId: TUUID, query?: TApiLineItemQuery, signal?: AbortSignal) =>
                request<TApiFileLineItemsResponse>(
                    `${MODULE_API.review}/files/${fileId}/line-items${buildQuery({ ...(query ?? {}) })}`,
                    { signal },
                ),
            // `query` carries the active column filters: the endpoint narrows every
            // facet list by the other filters in the group (see the endpoint docstring).
            getLineItemFilterOptions: (
                fileId: TUUID,
                options?: { glGeneratedOnly?: boolean; excludeGLGenerated?: boolean },
                query?: TApiLineItemQuery,
                signal?: AbortSignal,
            ) =>
                request<TApiLineItemFilterOptions>(
                    `${MODULE_API.review}/files/${fileId}/line-items/filter-options${buildQuery({
                        ...(query ?? {}),
                        gl_generated_only: options?.glGeneratedOnly,
                        exclude_gl_generated: options?.excludeGLGenerated,
                    })}`,
                    { signal },
                ),
            // Segment values selectable across a whole file: segments 5 and 6 come
            // from the custom lists of the accounts the file references, falling
            // back to the master list when none of them has one.
            getSegmentOptions: (
                fileId: TUUID,
                params: { segment_type: number; search?: string },
                signal?: AbortSignal,
            ) =>
                request<TApiSegmentOption[]>(
                    `${MODULE_API.review}/files/${fileId}/line-items/segment-options${buildQuery({
                        segment_type: params.segment_type,
                        search: params.search,
                    })}`,
                    { signal },
                ),
            updateLineItem: (fileId: TUUID, itemId: TUUID, data: Partial<TApiFileLineItem>) =>
                request<TApiFileLineItem>(
                    `${MODULE_API.review}/files/${fileId}/line-items/${itemId}`,
                    {
                        method: "PUT",
                        body: JSON.stringify(data),
                    },
                ),
            // Add `count` split rows to a line (creating the split, or appending
            // to an existing one). New rows start at amount 0.
            splitLineItem: (fileId: TUUID, parentId: TUUID, data: TApiSplitLineItemRequest) =>
                request<TApiSplitLineItemResponse>(
                    `${MODULE_API.review}/files/${fileId}/line-items/${parentId}/split`,
                    { method: "POST", body: JSON.stringify(data) },
                ),
            // Reverse a split: delete every split row, leaving the original as it was.
            reverseSplit: (fileId: TUUID, parentId: TUUID) =>
                request<TApiReverseSplitResponse>(
                    `${MODULE_API.review}/files/${fileId}/line-items/${parentId}/split`,
                    { method: "DELETE" },
                ),
            // Delete one split row. Only split rows can be deleted.
            deleteLineItem: (fileId: TUUID, itemId: TUUID) =>
                request<void>(`${MODULE_API.review}/files/${fileId}/line-items/${itemId}`, {
                    method: "DELETE",
                }),
            autoPopulatePropcodes: (fileId: TUUID, data: TApiAutoPopulatePropcodesRequest) =>
                request<TApiAutoPopulatePropcodesResponse>(
                    `${MODULE_API.review}/files/${fileId}/line-items/auto-populate-propcodes`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                ),
            autoPopulateSegments: (fileId: TUUID, data: TApiAutoPopulateSegmentsRequest) =>
                request<TApiAutoPopulateSegmentsResponse>(
                    `${MODULE_API.review}/files/${fileId}/line-items/auto-populate-segments`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                ),
            autoPopulatePropcodesByFilter: (
                fileId: TUUID,
                query: TApiLineItemQuery,
                data: TApiAutoPopulatePropcodesByFilterRequest,
            ) =>
                request<TApiAutoPopulatePropcodesResponse>(
                    `${MODULE_API.review}/files/${fileId}/line-items/auto-populate-propcodes/by-filter${buildQuery(
                        {
                            ...query,
                        },
                    )}`,
                    { method: "POST", body: JSON.stringify(data) },
                ),
            autoPopulateSegmentsByFilter: (
                fileId: TUUID,
                query: TApiLineItemQuery,
                data: TApiAutoPopulateSegmentsByFilterRequest,
            ) =>
                request<TApiAutoPopulateSegmentsResponse>(
                    `${MODULE_API.review}/files/${fileId}/line-items/auto-populate-segments/by-filter${buildQuery(
                        {
                            ...query,
                        },
                    )}`,
                    { method: "POST", body: JSON.stringify(data) },
                ),
            reprocess: (fileId: TUUID) =>
                request<TApiFileOperationAccepted>(
                    `${MODULE_API.workflow}/files/${fileId}/reprocess`,
                    {
                        method: "POST",
                    },
                ),
            revalidate: (fileId: TUUID, reapplyMappings = false) =>
                request<TApiFileOperationAccepted>(
                    `${MODULE_API.workflow}/files/${fileId}/revalidate${reapplyMappings ? "?reapply_mappings=true" : ""}`,
                    {
                        method: "POST",
                    },
                ),
            // Cheap poll target for a file's background operation (no line items
            // loaded), so polling every few seconds is acceptable.
            operationStatus: (fileId: TUUID) =>
                request<TApiFileOperationStatus>(
                    `${MODULE_API.workflow}/files/${fileId}/operation-status`,
                ),
            // Start a background revalidation of ALL the company's active files
            // (re-apply new master data); returns the initial progress snapshot.
            revalidateAll: () =>
                request<TApiRevalidationStatus>(`${MODULE_API.validation}/files/revalidate-all`, {
                    method: "POST",
                }),
            revalidationStatus: () =>
                request<TApiRevalidationStatus>(
                    `${MODULE_API.validation}/files/revalidation-status`,
                ),
            completeReview: (fileId: TUUID) =>
                request<{ id: TUUID; status: string; message: string }>(
                    `${MODULE_API.workflow}/files/${fileId}/complete-review`,
                    { method: "POST" },
                ),
            revertToReview: (fileId: TUUID) =>
                request<{ id: TUUID; status: string; message: string }>(
                    `${MODULE_API.workflow}/files/${fileId}/revert-to-review`,
                    { method: "POST" },
                ),
            markCompleted: (fileId: TUUID) =>
                request<{ id: TUUID; status: string; message: string }>(
                    `${MODULE_API.workflow}/files/${fileId}/complete`,
                    { method: "POST" },
                ),
            reopen: (fileId: TUUID) =>
                request<{ id: TUUID; status: string; message: string }>(
                    `${MODULE_API.workflow}/files/${fileId}/reopen`,
                    { method: "POST" },
                ),
            setContributors: (fileId: TUUID, contributorIds: TUUID[]) =>
                request<TApiFinancialFile>(`${MODULE_API.ingestion}/files/${fileId}/contributors`, {
                    method: "PUT",
                    body: JSON.stringify({ contributor_ids: contributorIds }),
                }),
            reject: async (
                fileId: TUUID,
                reason: string,
            ): Promise<{ id: TUUID; status: string; message: string }> => {
                const formData = new FormData();
                formData.append("reason", reason);
                const url = `${baseUrl}${MODULE_API.workflow}/files/${fileId}/reject`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: { ...(companyId && { "X-Company-ID": companyId }) },
                };
                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }
                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response
                        .json()
                        .catch(() => ({ detail: "Reject failed" }));
                    throw new ApiError(
                        errorBody?.detail || errorBody?.error?.message || "Reject failed",
                        response.status,
                        errorBody,
                    );
                }
                return response.json();
            },
            autoBalance: (fileId: TUUID) =>
                request<TApiFileOperationAccepted>(
                    `${MODULE_API.generation}/files/${fileId}/auto-balance`,
                    {
                        method: "POST",
                    },
                ),
            commit: (fileId: TUUID) =>
                request<TApiCommitResponse>(`${MODULE_API.workflow}/files/${fileId}/commit`, {
                    method: "POST",
                }),
            removeGeneratedLines: (fileId: TUUID) =>
                request<TApiFileOperationAccepted>(
                    `${MODULE_API.generation}/files/${fileId}/remove-generated-lines`,
                    {
                        method: "POST",
                    },
                ),
            runAdjustment: (fileId: TUUID) =>
                request<TApiFileOperationAccepted>(
                    `${MODULE_API.generation}/files/${fileId}/run-adjustment`,
                    {
                        method: "POST",
                    },
                ),
            generateGL: (fileId: TUUID) =>
                request<TApiFileOperationAccepted>(
                    `${MODULE_API.generation}/files/${fileId}/generate-gl`,
                    {
                        method: "POST",
                    },
                ),
            getSftpSubmissions: (fileId: TUUID) =>
                request<TApiFileSftpSubmission[]>(
                    `${MODULE_API.delivery}/files/${fileId}/sftp-submissions`,
                ),
            createSftpSubmission: (fileId: TUUID, data: TCreateFileSftpSubmissionRequest) =>
                request<TApiFileSftpSubmission>(
                    `${MODULE_API.delivery}/files/${fileId}/sftp-submissions`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                ),
            generateFinJournal: (fileId: TUUID) =>
                request<{ id: string; status: string; message: string }>(
                    `${MODULE_API.generation}/files/${fileId}/generate-finjournal`,
                    { method: "POST" },
                ),
            generateDataPlatform: (fileId: TUUID) =>
                request<{ id: string; status: string; message: string }>(
                    `${MODULE_API.generation}/files/${fileId}/generate-data-platform`,
                    { method: "POST" },
                ),
            getAvailableDownloads: (fileId: TUUID) =>
                request<{
                    file_id: string;
                    original_filename: string;
                    downloads: Array<{
                        type: string;
                        label: string;
                        available: boolean;
                        description: string | null;
                    }>;
                }>(`${MODULE_API.ingestion}/files/${fileId}/downloads`),
            downloadFile: (
                fileId: TUUID,
                downloadType: "original" | "processed" | "errors" | "finjournal" | "data-platform",
            ) =>
                downloadFromPath(
                    `${MODULE_API.ingestion}/files/${fileId}/download/${downloadType}`,
                ),
            /**
             * Download one specific file's original.
             *
             * `downloadFile` above is bound to whichever file the review page has
             * open; the Original Files tab lists a file per type and downloads any
             * of them, so it needs the id passed in rather than implied.
             */
            downloadOriginal: (fileId: TUUID) =>
                downloadFromPath(`${MODULE_API.ingestion}/files/${fileId}/download/original`),
            /** Fetch one file's original upload as a `File` (no browser download). */
            fetchOriginal: (fileId: TUUID) =>
                fetchFileFromPath(`${MODULE_API.ingestion}/files/${fileId}/download/original`),
            /** Download a period's generated ETL export. */
            downloadGeneratedExport: (fileId: TUUID, kind: "finjournal" | "data_platform") =>
                downloadFromPath(
                    `${MODULE_API.delivery}/files/${fileId}/generated-exports/${kind}/download`,
                ),
            /** Download a TB's operating statement as Excel, optionally for one property. */
            downloadOperatingStatement: (fileId: TUUID, propertyCode?: string) =>
                downloadFromPath(
                    `${MODULE_API.review}/files/${fileId}/download/operating-statement${buildQuery({
                        property_code: propertyCode,
                    })}`,
                ),
        },

        // Exports
        exports: {
            exportFile: (fileId: TUUID, data: TExportRequest) =>
                request<TApiExportResponse>(
                    `${MODULE_API.delivery}/exports/files/${fileId}/export`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                ),
            exportFileSync: (fileId: TUUID, data: TExportRequest) =>
                request<TApiExportResponse>(
                    `${MODULE_API.delivery}/exports/files/${fileId}/export/sync`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                ),
            getStatus: (fileId: TUUID) =>
                request<TApiExportResponse>(
                    `${MODULE_API.delivery}/exports/files/${fileId}/export/status`,
                ),
            getPreview: (fileId: TUUID, limit?: number) =>
                request<{ headers: string[]; rows: string[][] }>(
                    `${MODULE_API.delivery}/exports/files/${fileId}/export/preview${buildQuery({ limit })}`,
                ),
            retry: (fileId: TUUID, data: TExportRequest) =>
                request<TApiExportResponse>(
                    `${MODULE_API.delivery}/exports/files/${fileId}/export/retry`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                ),
        },

        // Sync
        sync: {
            full: () =>
                request<TApiSyncResponse>(`${MODULE_API.master_data}/sync/full`, {
                    method: "POST",
                }),
            chartOfAccounts: () =>
                request<TApiSyncResponse>(`${MODULE_API.master_data}/sync/chart-of-accounts`, {
                    method: "POST",
                }),
            propertyCodes: () =>
                request<TApiSyncResponse>(`${MODULE_API.master_data}/sync/property-codes`, {
                    method: "POST",
                }),
            segmentValues: () =>
                request<TApiSyncResponse>(`${MODULE_API.master_data}/sync/segment-values`, {
                    method: "POST",
                }),
            getStatus: (syncType?: string) =>
                request<Record<string, unknown>>(
                    `${MODULE_API.master_data}/sync/status${buildQuery({ sync_type: syncType })}`,
                ),
            getHistory: (params?: { sync_type?: string; skip?: number; limit?: number }) =>
                request<TPaginatedResponse<Record<string, unknown>>>(
                    `${MODULE_API.master_data}/sync/history${buildQuery(params || {})}`,
                ),
        },

        // Reference Data
        chartOfAccounts: {
            list: (
                params?: {
                    page?: number;
                    page_size?: number;
                    active_only?: boolean;
                    name?: string;
                    number?: string;
                    category?: string;
                    is_prohibited?: boolean;
                    is_active?: boolean;
                    sort_by?: string;
                    sort_dir?: "asc" | "desc";
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiChartOfAccounts>>(
                    `${MODULE_API.mappings}/chart-of-accounts${buildQuery(params || {})}`,
                    { signal },
                ),
            listPaginated: (params?: {
                search?: string;
                page?: number;
                page_size?: number;
                active_only?: boolean;
            }) =>
                request<TApiPaginatedChartOfAccounts>(
                    `${MODULE_API.mappings}/chart-of-accounts/paginated${buildQuery(params || {})}`,
                ),
            upload: async (file: File): Promise<TApiCoAMasterUploadResponse> => {
                const formData = new FormData();
                formData.append("file", file);

                const url = `${baseUrl}${MODULE_API.mappings}/chart-of-accounts/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            create: (data: TCreateChartOfAccountsRequest) =>
                request<TApiChartOfAccounts>(`${MODULE_API.mappings}/chart-of-accounts`, {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdateChartOfAccountsRequest) =>
                request<TApiChartOfAccounts>(`${MODULE_API.mappings}/chart-of-accounts/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            delete: (id: TUUID) =>
                request<void>(`${MODULE_API.mappings}/chart-of-accounts/${id}`, {
                    method: "DELETE",
                }),
            usage: (id: TUUID) =>
                request<TApiMasterCodeUsageResponse>(
                    `${MODULE_API.mappings}/chart-of-accounts/${id}/usage`,
                ),
        },
        books: {
            list: (params?: {
                business_partner_type?: string;
                active_only?: boolean;
                skip?: number;
                limit?: number;
            }) =>
                request<TPaginatedResponse<TApiBook>>(
                    `${MODULE_API.partners}/books${buildQuery(params || {})}`,
                ),
            create: (data: TCreateBookRequest) =>
                request<TApiBook>(`${MODULE_API.partners}/books`, {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdateBookRequest) =>
                request<TApiBook>(`${MODULE_API.partners}/books/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            delete: (id: TUUID) =>
                request<void>(`${MODULE_API.partners}/books/${id}`, { method: "DELETE" }),
            upload: async (file: File): Promise<TApiBookMasterUploadResponse> => {
                const formData = new FormData();
                formData.append("file", file);

                const url = `${baseUrl}${MODULE_API.partners}/books/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
        },
        yardiImport: {
            getStatus: () =>
                request<TApiYardiImportStatus>(`${MODULE_API.master_data}/yardi-import/status`),
            uploadFile: async (
                sourceTable: string,
                file: File,
            ): Promise<TApiYardiFileUploadResponse> => {
                const formData = new FormData();
                formData.append("file", file);

                const url = `${baseUrl}${MODULE_API.master_data}/yardi-import/files/${sourceTable}/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            generate: (tab: string) =>
                request<TApiYardiGenerateResponse>(
                    `${MODULE_API.master_data}/yardi-import/generate/${tab}`,
                    {
                        method: "POST",
                    },
                ),
        },
        propertyCodes: {
            list: (
                params?: {
                    page?: number;
                    page_size?: number;
                    active_only?: boolean;
                    code?: string;
                    description?: string;
                    is_active?: boolean;
                    kind?: string;
                    sort_by?: string;
                    sort_dir?: "asc" | "desc";
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiPropertyCode>>(
                    `${MODULE_API.mappings}/property-codes${buildQuery(params || {})}`,
                    { signal },
                ),
            create: (data: TCreatePropertyCodeRequest) =>
                request<TApiPropertyCode>(`${MODULE_API.mappings}/property-codes`, {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdatePropertyCodeRequest) =>
                request<TApiPropertyCode>(`${MODULE_API.mappings}/property-codes/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            delete: (id: TUUID) =>
                request<void>(`${MODULE_API.mappings}/property-codes/${id}`, { method: "DELETE" }),
            usage: (id: TUUID) =>
                request<TApiMasterCodeUsageResponse>(
                    `${MODULE_API.mappings}/property-codes/${id}/usage`,
                ),
            upload: async (file: File): Promise<TApiPropcodeMasterUploadResponse> => {
                const formData = new FormData();
                formData.append("file", file);

                const url = `${baseUrl}${MODULE_API.mappings}/property-codes/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            listPaginated: (params?: {
                search?: string;
                page?: number;
                page_size?: number;
                active_only?: boolean;
                kind?: string;
            }) =>
                request<TApiPaginatedPropertyCodes>(
                    `${MODULE_API.mappings}/property-codes/paginated${buildQuery(params || {})}`,
                ),
        },
        segmentValues: {
            list: (
                segmentNumber?: number,
                params?: {
                    page?: number;
                    page_size?: number;
                    active_only?: boolean;
                    platform_value?: string;
                    is_active?: boolean;
                    sort_by?: string;
                    sort_dir?: "asc" | "desc";
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiSegmentValue>>(
                    `${MODULE_API.mappings}/segment-values${buildQuery({
                        segment_number: segmentNumber,
                        page: params?.page,
                        page_size: params?.page_size,
                        active_only: params?.active_only,
                        platform_value: params?.platform_value,
                        is_active: params?.is_active,
                        sort_by: params?.sort_by,
                        sort_dir: params?.sort_dir,
                    })}`,
                    { signal },
                ),
            create: (data: TCreateSegmentValueRequest) =>
                request<TApiSegmentValue>(`${MODULE_API.mappings}/segment-values`, {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdateSegmentValueRequest) =>
                request<TApiSegmentValue>(`${MODULE_API.mappings}/segment-values/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            delete: (id: TUUID) =>
                request<void>(`${MODULE_API.mappings}/segment-values/${id}`, { method: "DELETE" }),
            upload: async (file: File): Promise<TApiSegmentMasterUploadResponse> => {
                const formData = new FormData();
                formData.append("file", file);

                const url = `${baseUrl}${MODULE_API.mappings}/segment-values/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response
                        .json()
                        .catch(() => ({ detail: "Segment master upload failed" }));
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
        },
        segmentCustomValues: {
            list: (
                params?: {
                    segment_type?: number;
                    account_code?: string;
                    code?: string;
                    property_code?: string;
                    is_active?: boolean;
                    active_only?: boolean;
                    page?: number;
                    page_size?: number;
                    sort_by?: string;
                    sort_dir?: "asc" | "desc";
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiSegmentCustomValue>>(
                    `${MODULE_API.mappings}/segment-custom-values${buildQuery(params ?? {})}`,
                    { signal },
                ),
            create: (data: TCreateSegmentCustomValueRequest) =>
                request<TApiSegmentCustomValue>(`${MODULE_API.mappings}/segment-custom-values`, {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdateSegmentCustomValueRequest) =>
                request<TApiSegmentCustomValue>(
                    `${MODULE_API.mappings}/segment-custom-values/${id}`,
                    {
                        method: "PUT",
                        body: JSON.stringify(data),
                    },
                ),
            delete: (id: TUUID) =>
                request<void>(`${MODULE_API.mappings}/segment-custom-values/${id}`, {
                    method: "DELETE",
                }),
        },
        segmentationMappings: {
            list: (
                params?: {
                    active_only?: boolean;
                    account_code?: string;
                    is_active?: boolean;
                    is_mandatory_segment_2?: boolean;
                    has_custom_segment_2?: boolean;
                    page?: number;
                    page_size?: number;
                    sort_by?: string;
                    sort_dir?: "asc" | "desc";
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiSegmentationMapping>>(
                    `${MODULE_API.mappings}/segmentation-mappings${buildQuery(params || {})}`,
                    { signal },
                ),
            getByAccount: (accountCode: string) =>
                request<TApiSegmentationMapping>(
                    `${MODULE_API.mappings}/segmentation-mappings/account/${encodeURIComponent(accountCode)}`,
                ),
            getAllowedValues: (
                accountCode: string,
                segmentType: number,
                propertyCode?: string | null,
            ) =>
                request<string[]>(
                    `${MODULE_API.mappings}/segmentation-mappings/account/${encodeURIComponent(accountCode)}/allowed-values${buildQuery({ segment_type: segmentType, property_code: propertyCode ?? undefined })}`,
                ),
            getAllowedValuesBatch: (accountCode: string, params?: { property_code?: string }) =>
                request<TApiAllowedSegmentValues>(
                    `${MODULE_API.mappings}/segmentation-mappings/account/${encodeURIComponent(accountCode)}/allowed-values-batch${buildQuery({ property_code: params?.property_code })}`,
                ),
            create: (data: TCreateSegmentationMappingRequest) =>
                request<TApiSegmentationMapping>(`${MODULE_API.mappings}/segmentation-mappings`, {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdateSegmentationMappingRequest) =>
                request<TApiSegmentationMapping>(
                    `${MODULE_API.mappings}/segmentation-mappings/${id}`,
                    {
                        method: "PUT",
                        body: JSON.stringify(data),
                    },
                ),
            delete: (id: TUUID) =>
                request<void>(`${MODULE_API.mappings}/segmentation-mappings/${id}`, {
                    method: "DELETE",
                }),
            upload: async (file: File): Promise<TApiSegmentRulesUploadResponse> => {
                const formData = new FormData();
                formData.append("file", file);

                const url = `${baseUrl}${MODULE_API.mappings}/segmentation-mappings/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response
                        .json()
                        .catch(() => ({ detail: "Segment rules upload failed" }));
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
        },

        // Announcements
        announcements: {
            list: (params?: { skip?: number; limit?: number }) =>
                request<TPaginatedResponse<TApiAnnouncement>>(
                    `/v1/announcements${buildQuery(params || {})}`,
                ),
            get: (id: TUUID) => request<TApiAnnouncement>(`/v1/announcements/${id}`),
            getActive: () => request<TApiAnnouncement | null>("/v1/announcements/active"),
            create: (data: TCreateAnnouncementRequest) =>
                request<TApiAnnouncement>("/v1/announcements", {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdateAnnouncementRequest) =>
                request<TApiAnnouncement>(`/v1/announcements/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            delete: (id: TUUID) =>
                request<void>(`/v1/announcements/${id}`, {
                    method: "DELETE",
                }),
            dismiss: (id: TUUID) =>
                request<void>(`/v1/announcements/${id}/dismiss`, {
                    method: "POST",
                }),
        },

        // Supporting Files
        supportingFiles: {
            list: (
                params?: {
                    deal_id?: TUUID;
                    financial_file_id?: TUUID;
                    deal_ids?: string[];
                    period_from?: string;
                    period_to?: string;
                    related_file?: string;
                    friendly_name?: string;
                    original_filename?: string;
                    notes?: string;
                    uploaded_by?: string;
                    sort_by?: string;
                    sort_dir?: string;
                    skip?: number;
                    limit?: number;
                },
                signal?: AbortSignal,
            ) =>
                request<TPaginatedResponse<TApiSupportingFile>>(
                    `${MODULE_API.attachments}/supporting-files${buildQuery(params || {})}`,
                    { signal },
                ),
            filterOptions: () =>
                request<TApiSupportingFileFilterOptions>(
                    `${MODULE_API.attachments}/supporting-files/filter-options`,
                ),
            relatableFiles: (dealId: TUUID, signal?: AbortSignal) =>
                request<Array<{ id: TUUID; name: string }>>(
                    `${MODULE_API.attachments}/supporting-files/relatable-files${buildQuery({ deal_id: dealId })}`,
                    { signal },
                ),
            get: (id: TUUID) =>
                request<TApiSupportingFile>(`${MODULE_API.attachments}/supporting-files/${id}`),
            update: (
                id: TUUID,
                data: {
                    friendly_name?: string;
                    notes?: string | null;
                    financial_file_id?: TUUID | null;
                    clear_financial_file?: boolean;
                    period?: string;
                    row_version?: string | null;
                },
            ) =>
                request<TApiSupportingFile>(`${MODULE_API.attachments}/supporting-files/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                    headers: { "Content-Type": "application/json" },
                }),
            upload: async (
                file: File,
                metadata: {
                    deal_id: TUUID;
                    friendly_name: string;
                    period: string;
                    financial_file_id?: TUUID;
                    notes?: string;
                },
            ): Promise<TApiSupportingFile> => {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("deal_id", metadata.deal_id);
                formData.append("friendly_name", metadata.friendly_name);
                formData.append("period", metadata.period);
                if (metadata.financial_file_id) {
                    formData.append("financial_file_id", metadata.financial_file_id);
                }
                if (metadata.notes) {
                    formData.append("notes", metadata.notes);
                }

                const url = `${baseUrl}${MODULE_API.attachments}/supporting-files/upload`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
            download: async (id: TUUID) => {
                const url = `${baseUrl}${MODULE_API.attachments}/supporting-files/${id}/download`;
                let requestInit: RequestInit = {
                    method: "GET",
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }

                const contentDisposition = response.headers.get("Content-Disposition");
                let filename = "download";
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(
                        /filename\*?=(?:UTF-8'')?([^;]+)/i,
                    );
                    if (filenameMatch?.[1]) {
                        filename = decodeURIComponent(filenameMatch[1].replace(/["']/g, ""));
                    }
                }

                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            },
            delete: (id: TUUID) =>
                request<void>(`${MODULE_API.attachments}/supporting-files/${id}`, {
                    method: "DELETE",
                }),
        },

        // FinJournals (Standalone)
        finjournals: {
            // Users the caller may add as contributors, scoped by their role.
            // Pass a deal for a deal-based FinJournal; omit for standalone.
            getContributorCandidates: (dealId?: TUUID | null) =>
                request<TApiContributorEntry[]>(
                    `${MODULE_API.delivery}/finjournals/contributor-candidates${buildQuery({ deal_id: dealId ?? undefined })}`,
                ),
            uploadStandalone: async (
                file: File,
                options?: { sheetName?: string; dateFormat?: string; contributorIds?: TUUID[] },
            ): Promise<{
                id: string;
                filename: string;
                status: string;
                uploaded_at: string | null;
                message: string;
            }> => {
                const formData = new FormData();
                formData.append("file", file);
                if (options?.sheetName) {
                    formData.append("sheet_name", options.sheetName);
                }
                if (options?.dateFormat) {
                    formData.append("date_format", options.dateFormat);
                }
                // Repeated form fields → FastAPI list[TUUID] = Form(...)
                for (const contributorId of options?.contributorIds ?? []) {
                    formData.append("contributor_ids", contributorId);
                }

                const url = `${baseUrl}${MODULE_API.delivery}/finjournals/`;
                let requestInit: RequestInit = {
                    method: "POST",
                    body: formData,
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                return response.json();
            },
        },

        // Support Tickets
        supportTickets: {
            list: (params?: {
                page?: number;
                page_size?: number;
                status?: TApiSupportTicketStatus;
            }) =>
                request<TApiSupportTicketListResponse>(
                    `/v1/support-tickets${buildQuery(params || {})}`,
                ),
            get: (id: TUUID) => request<TApiSupportTicketResponse>(`/v1/support-tickets/${id}`),
            create: (data: TCreateSupportTicketRequest) =>
                request<TApiSupportTicketResponse>("/v1/support-tickets", {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            update: (id: TUUID, data: TUpdateSupportTicketRequest) =>
                request<TApiSupportTicketResponse>(`/v1/support-tickets/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            close: (id: TUUID) =>
                request<TApiSupportTicketResponse>(`/v1/support-tickets/${id}/close`, {
                    method: "POST",
                }),
            adminAction: (id: TUUID, data: TSupportTicketAdminActionRequest) =>
                request<TApiSupportTicketResponse>(`/v1/support-tickets/${id}/admin-action`, {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            listComments: (id: TUUID, params?: { skip?: number; limit?: number }) =>
                request<TPaginatedResponse<TApiSupportTicketCommentResponse>>(
                    `/v1/support-tickets/${id}/comments${buildQuery(params || {})}`,
                ),
            createComment: (id: TUUID, data: TCreateSupportTicketCommentRequest) =>
                request<TApiSupportTicketCommentResponse>(`/v1/support-tickets/${id}/comments`, {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
            listForwarded: (params?: { page?: number; page_size?: number; company_id?: TUUID }) =>
                request<TApiSupportTicketListResponse>(
                    `/v1/support-tickets/forwarded${buildQuery(params || {})}`,
                ),
        },

        feedback: {
            submit: (data: TCreateFeedbackRequest): Promise<TFeedbackResponse> =>
                request<TFeedbackResponse>("/v1/feedback", {
                    method: "POST",
                    body: JSON.stringify(data),
                }),
        },

        // Company Settings (thresholds: company admins; SFTP settings/schedules: Proda admins only)
        companySettings: {
            listThresholds: () =>
                request<TApiFileProcessingThreshold[]>(
                    `${MODULE_API.delivery}/file-processing-thresholds`,
                ),
            createThreshold: (data: TCreateFileProcessingThresholdRequest) =>
                request<TApiFileProcessingThreshold>(
                    `${MODULE_API.delivery}/file-processing-thresholds`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                ),
            updateThreshold: (id: TUUID, data: TUpdateFileProcessingThresholdRequest) =>
                request<TApiFileProcessingThreshold>(
                    `${MODULE_API.delivery}/file-processing-thresholds/${id}`,
                    {
                        method: "PUT",
                        body: JSON.stringify(data),
                    },
                ),
            deleteThreshold: (id: TUUID) =>
                request<void>(`${MODULE_API.delivery}/file-processing-thresholds/${id}`, {
                    method: "DELETE",
                }),
            listSftpSubmissions: () =>
                request<TApiSftpFileSubmission[]>(`${MODULE_API.delivery}/sftp-file-submissions`),
            downloadSftpTransferPayload: async (transferId: TUUID) => {
                const url = `${baseUrl}${MODULE_API.delivery}/sftp-transfers/${transferId}/download`;
                let requestInit: RequestInit = {
                    method: "GET",
                    headers: {
                        ...(companyId && { "X-Company-ID": companyId }),
                    },
                };

                for (const interceptor of requestInterceptors) {
                    requestInit = await interceptor(requestInit);
                }

                const response = await authFetch(url, requestInit);
                if (!response.ok) {
                    const errorBody = await response.json().catch(() => undefined);
                    const errorMessage = messageFromProdaErrorBody(errorBody, response.status);
                    throw new ApiError(errorMessage, response.status, errorBody);
                }
                const contentDisposition = response.headers.get("Content-Disposition");
                let filename = "sftp-upload";
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(
                        /filename\*?=(?:UTF-8'')?([^;]+)/i,
                    );
                    if (filenameMatch?.[1]) {
                        filename = decodeURIComponent(filenameMatch[1].replace(/["']/g, ""));
                    }
                }

                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            },
            testSftpConnection: () =>
                request<TApiSftpConnectionTestResponse>(
                    `${MODULE_API.delivery}/company-sftp-settings/test-connection`,
                    { method: "POST" },
                ),
            getSftpSettings: () =>
                request<TApiCompanySftpSettingsGetResponse>(
                    `${MODULE_API.delivery}/company-sftp-settings`,
                ),
            updateSftpSettings: (data: TUpdateCompanySftpSettingsRequest) =>
                request<TApiCompanySftpSettings>(`${MODULE_API.delivery}/company-sftp-settings`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                }),
            getCompanyYardiMasterDataSettings: () =>
                request<TApiCompanyYardiMasterDataSettingsGetResponse>(
                    `${MODULE_API.delivery}/company-yardi-master-data-settings`,
                ),
            putCompanyYardiMasterDataSettings: (
                payload: TApiCompanyYardiMasterDataSettingsUpdate,
            ) =>
                request<TApiCompanyYardiMasterDataSettings>(
                    `${MODULE_API.delivery}/company-yardi-master-data-settings`,
                    {
                        method: "PUT",
                        body: JSON.stringify(payload),
                    },
                ),
            triggerYardiMasterDataSync: () =>
                request<TApiYardiMasterDataSyncTriggered>(
                    `${MODULE_API.master_data}/yardi-master-data/sync`,
                    {
                        method: "POST",
                    },
                ),
            listYardiMasterDataSyncRuns: (page = 1, pageSize = 20) =>
                request<TApiYardiMasterDataSyncRunList>(
                    `${MODULE_API.master_data}/yardi-master-data/sync-runs?page=${page}&page_size=${pageSize}`,
                ),
        },

        // Audit Log
        auditLog: {
            /** Company-scoped audit log (admin sees all; non-admin sees their deals) */
            list: (query?: TAuditLogQuery, signal?: AbortSignal) =>
                request<TCappedPaginatedResponse<TApiCompanyAuditLogEntry>>(
                    `/v1/audit-log${buildQuery(query || {})}`,
                    { signal },
                ),
            /** Global audit log across all companies (Proda admin only) */
            listGlobal: (query?: TAuditLogQuery, signal?: AbortSignal) =>
                request<TCappedPaginatedResponse<TApiAuditLogEntry>>(
                    `/v1/admin/audit-log${buildQuery(query || {})}`,
                    { signal },
                ),
            /** Dynamic filter options for the company audit log */
            filterOptions: () => request<TApiAuditLogFilterOptions>("/v1/audit-log/filter-options"),
            /** Dynamic filter options for the global audit log (Proda admin only) */
            filterOptionsGlobal: () =>
                request<TApiAuditLogFilterOptions>("/v1/admin/audit-log/filter-options"),
            /** Download the company audit log for a CET date range as .xlsx (Product Owner only) */
            exportExcel: (params: TAuditLogExportParams) =>
                downloadFromPath(
                    `/v1/audit-log/export${buildQuery({
                        date_from: params.dateFrom,
                        date_to: params.dateTo,
                        category: params.categories,
                    })}`,
                ),
        },

        // Email Logs (outbound email communications, Product Owner only)
        emailLogs: {
            /** Company-scoped email send log, newest first */
            list: (query?: TEmailLogQuery, signal?: AbortSignal) =>
                request<TPaginatedResponse<TApiEmailLogEntry>>(
                    `/v1/email-logs${buildQuery(query || {})}`,
                    { signal },
                ),
        },
    };
}

/**
 * API client type
 */
export type TApiClient = ReturnType<typeof createApiClient>;
